import type { GraphEdge, GraphNode, LinkGraph } from '@/api/services/links'
import { textDocumentPath } from '@/routes/paths'

export const GRAPH_NODE_COLORS = new Map<GraphNode['type'], string>([
  ['book', '#3b82f6'],
  ['card', '#f59e0b'],
  ['memo', '#9ca3af'],
  ['todo', '#10b981'],
])

const ENTITY_TYPE_OF_NODE = new Map<GraphNode['type'], string>([
  ['card', 'memo'],
  ['memo', 'memo'],
  ['book', 'product_note'],
  ['todo', 'todo'],
])

/** 点をタップしたときに開く画面 */
export const graphNodePath = (node: GraphNode): string | null => {
  const entityType = ENTITY_TYPE_OF_NODE.get(node.type)
  return entityType ? textDocumentPath(entityType, node.ref_id) : null
}

const neighborsOf = (edges: GraphEdge[]): Map<string, Set<string>> => {
  const neighbors = new Map<string, Set<string>>()
  const add = (a: string, b: string) => neighbors.set(a, (neighbors.get(a) ?? new Set()).add(b))
  for (const edge of edges) {
    add(edge.source, edge.target)
    add(edge.target, edge.source)
  }
  return neighbors
}

/** centerId から depth 段以内の点と、その間の線（向きは問わない） */
export const localSubgraph = (graph: LinkGraph, centerId: string, depth = 2): LinkGraph => {
  const neighbors = neighborsOf(graph.edges)
  const reached = new Set([centerId])
  let frontier = [centerId]
  for (let step = 0; step < depth; step++) {
    frontier = frontier.flatMap((id) => [...(neighbors.get(id) ?? [])].filter((n) => !reached.has(n)))
    for (const id of frontier) reached.add(id)
  }
  return {
    nodes: graph.nodes.filter((n) => reached.has(n.id)),
    edges: graph.edges.filter((e) => reached.has(e.source) && reached.has(e.target)),
  }
}

export type BookPair = {
  source: GraphNode
  target: GraphNode
  /** 2 冊をつないでいるカード・メモ（本どうしの直接のリンクは含まない） */
  via: GraphNode[]
  /** 共通するカード・メモの数 + 直接のリンクの数 */
  weight: number
}

/** 同じカード・メモとつながっている本どうしを結ぶ。共通する点が多いほど太くする */
export const bookPairs = (graph: LinkGraph): BookPair[] => {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]))
  const isBook = (id: string) => byId.get(id)?.type === 'book'
  const neighbors = neighborsOf(graph.edges)
  const pairs = new Map<string, BookPair>()

  const pairOf = (a: string, b: string): BookPair | null => {
    const [s, t] = a < b ? [a, b] : [b, a]
    const source = byId.get(s)
    const target = byId.get(t)
    if (!source || !target) return null
    const key = `${s}|${t}`
    const pair = pairs.get(key) ?? { source, target, via: [], weight: 0 }
    pairs.set(key, pair)
    return pair
  }

  for (const node of graph.nodes) {
    if (node.type === 'book') continue
    const books = [...(neighbors.get(node.id) ?? [])].filter(isBook)
    books.forEach((a, i) => {
      for (const b of books.slice(i + 1)) {
        const pair = pairOf(a, b)
        if (!pair) continue
        pair.via.push(node)
        pair.weight += 1
      }
    })
  }
  for (const edge of graph.edges) {
    if (!isBook(edge.source) || !isBook(edge.target)) continue
    const pair = pairOf(edge.source, edge.target)
    if (pair) pair.weight += 1
  }
  return [...pairs.values()].sort((a, b) => b.weight - a.weight)
}

/** 本どうしの図に使うグラフ（点は本だけ） */
export const bookPairGraph = (graph: LinkGraph, pairs: BookPair[]): LinkGraph => {
  const ids = new Set(pairs.flatMap((p) => [p.source.id, p.target.id]))
  return {
    nodes: graph.nodes.filter((n) => ids.has(n.id)),
    edges: pairs.map((p) => ({ source: p.source.id, target: p.target.id, kind: 'link', weight: p.weight })),
  }
}
